import { ComponentType } from '@angular/cdk/portal';
import { Injectable, NgZone } from '@angular/core';
import { DialogPosition, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import {
  ConfirmationModalBodyImg,
  ConfirmationModalComponent,
} from '../standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { ConsentModalComponent } from '../standalones/components/modals/consent-modal/consent-modal.component';
import {
  GraphicModalCloseResultEnum,
  GraphicModalComponent,
  GraphicModalData,
} from '../standalones/components/modals/graphic-modal/graphic-modal.component';
import {
  PencilModalSizeEnum,
  PencilModalSizeMapping,
} from '../standalones/components/modals/modal-template/modal-template.component';
import { PencilButtonActionConfig } from '../standalones/components/pencil-button/pencil-button.component';
import { IconTypes } from '../standalones/components/pencil-icon/pencil-icon.component';

export enum ModalBackdropPreset {
  DEFAULT = 'DEFAULT',
  BLOCKING = 'BLOCKING',
}

type BackdropConfig = Pick<MatDialogConfig, 'hasBackdrop' | 'backdropClass' | 'disableClose'>;

class CallbackQueue {
  private readonly queue: (() => void)[] = [];

  public enqueue(cb: () => void) {
    this.queue.push(cb);
    // There is only one item in the queue run it immediately
    if (this.queue.length === 1) {
      cb();
    }
  }

  public dequeue() {
    this.queue.splice(0, 1);

    if (this.queue.length > 0) {
      const cb = this.queue[0];
      cb();
    }
  }
}

interface ShowModalOptions<T> {
  beforeOpened?: () => void;
  afterClosed?: (val: T) => void;
  afterOpened?: () => void;
  positionRelative?: HTMLElement | null;
}

export interface ConfirmationModalConfig {
  title: string;
  description: string;
  disableClose?: boolean;
  confirmButtonConfig?: ConfirmButtonConfig;
  /** When true and `bodyImg` is set, shows a full-bleed hero image above the padded description. */
  showBodyImg?: boolean;
  bodyImg?: ConfirmationModalBodyImg;
  descriptionGap?: boolean;
}

export interface ConsentModalConfig extends ConfirmationModalConfig {
  consentCheckboxLabel: string;
  size?: PencilModalSizeEnum;
}

type ConfirmButtonConfig = PencilButtonActionConfig;

export enum PresetConfirmActionEnum {
  DELETE = 'DELETE',
  ARCHIVE = 'ARCHIVE',
}

@Injectable({
  providedIn: 'root',
})
export class ModalManagerService {
  constructor(
    private matDialog: MatDialog,
    private ngZone: NgZone,
    private translateService: TranslateService,
  ) {}

  private readonly queue = new CallbackQueue();

  private static readonly BACKDROP_PRESET_MAP: Record<ModalBackdropPreset, BackdropConfig> = {
    [ModalBackdropPreset.DEFAULT]: {},
    [ModalBackdropPreset.BLOCKING]: {
      hasBackdrop: true,
      backdropClass: 'blocking-modal-backdrop',
      disableClose: true,
    },
  };

  getBackdropPreset(preset: ModalBackdropPreset): BackdropConfig {
    return { ...ModalManagerService.BACKDROP_PRESET_MAP[preset] };
  }

  /**
   * Map of preset configurations to confirm button configurations
   */
  private readonly presetConfirmButtonConfigsMap: Record<
    PresetConfirmActionEnum,
    ConfirmButtonConfig
  > = {
    [PresetConfirmActionEnum.DELETE]: {
      icon: 'delete_outline',
      iconType: IconTypes.matIcon,
      label: this.translateService.instant('Delete'),
      danger: true,
    },
    [PresetConfirmActionEnum.ARCHIVE]: {
      icon: 'archive',
      iconType: IconTypes.materialIcon,
      outlined: true,
      label: this.translateService.instant('Archive'),
      danger: true,
    },
  };

  public showModal<T, D = any, R = any>(
    component: ComponentType<T>,
    dialogConfig?: MatDialogConfig<D>,
    modalOptions?: ShowModalOptions<R>,
  ) {
    const boundingBox: DOMRect | undefined =
      modalOptions?.positionRelative?.getBoundingClientRect();
    if (boundingBox) {
      const config: MatDialogConfig = dialogConfig || {};
      const position: DialogPosition = dialogConfig?.position || {};
      position.top = position.top
        ? `calc(${position.top} + ${boundingBox.bottom}px)`
        : `${boundingBox.bottom}px`;
      config.position = position;
      dialogConfig = config;
    }

    this.queue.enqueue(() => {
      if (modalOptions?.beforeOpened) {
        modalOptions.beforeOpened();
      }
      // Hotfix: To force any opened modal to run inside the zone
      this.ngZone.run(() => {
        const openedDialog = this.matDialog.open(component, dialogConfig);
        openedDialog
          .afterClosed()
          .pipe(first())
          .subscribe((val) => {
            this.queue.dequeue();
            if (modalOptions?.afterClosed) {
              modalOptions.afterClosed(val);
            }
          });
        openedDialog
          .afterOpened()
          .pipe(first())
          .subscribe(() => {
            if (modalOptions?.afterOpened) {
              modalOptions.afterOpened();
            }
          });
      });
    });
  }

  /**
   * Shows a generic confirmation dialog
   * @param config Configuration for the confirmation dialog
   * @returns Observable<boolean> - true if confirmed, false if cancelled
   */
  showConfirmationModal(config: ConfirmationModalConfig): Observable<boolean> {
    const { title, description } = config;

    const defaultConfirmButtonConfig = {
      icon: 'check',
      iconType: IconTypes.matIcon,
      label: this.translateService.instant('Confirm'),
    };

    const dialogData = {
      title: title,
      description: description,
      hasCloseButton: false,
      descriptionGap: config.descriptionGap ?? true,
      showBodyImg: config.showBodyImg ?? false,
      ...(config.bodyImg ? { bodyImg: config.bodyImg } : {}),
      confirmButtonConfig: {
        ...defaultConfirmButtonConfig,
        ...config.confirmButtonConfig,
      },
      cancelButtonConfig: {
        icon: 'close',
        iconType: IconTypes.matIcon,
        label: this.translateService.instant('Cancel'),
      },
    };

    const dialogConfig: MatDialogConfig = {
      width: PencilModalSizeEnum.MEDIUM,
      data: dialogData,
      disableClose: config.disableClose || false,
    };

    return this.matDialog
      .open(ConfirmationModalComponent, dialogConfig)
      .afterClosed()
      .pipe(map((result) => result === 'confirm'));
  }

  /**
   * Shows a consent dialog — a confirmation dialog with a required checkbox
   * that gates the confirm button.
   * @param config Configuration for the consent dialog
   * @returns Observable<boolean> - true if confirmed (checkbox ticked), false otherwise
   */
  showConsentModal(config: ConsentModalConfig): Observable<boolean> {
    const { title, description, consentCheckboxLabel, size } = config;

    const defaultConfirmButtonConfig = {
      icon: 'arrow_forward',
      iconType: IconTypes.matIcon,
      label: this.translateService.instant('Continue'),
    };

    const dialogData = {
      title,
      description,
      consentCheckboxLabel,
      hasCloseButton: true,
      confirmButtonConfig: { ...defaultConfirmButtonConfig, ...config.confirmButtonConfig },
      cancelButtonConfig: {
        icon: 'close',
        iconType: IconTypes.matIcon,
        label: this.translateService.instant('Cancel'),
      },
      size,
    };

    const dialogConfig: MatDialogConfig = {
      data: dialogData,
      disableClose: config.disableClose || false,
      // Only set explicit width when a size was provided; otherwise let MatDialog
      // and the component's default (SMALL) decide.
      width: size ? PencilModalSizeMapping[size] : undefined,
    };

    return this.matDialog
      .open(ConsentModalComponent, dialogConfig)
      .afterClosed()
      .pipe(map((result) => result === 'confirm'));
  }

  /**
   * Shows a generic graphic dialog: graphic (icon or full-bleed image) -> title ->
   * description -> 1-2 CTAs. Desktop/tablet only.
   * @param config Configuration for the graphic dialog
   * @returns Observable<GraphicModalCloseResultEnum | undefined> - which action closed the dialog
   */
  showGraphicModal(
    config: GraphicModalData,
  ): Observable<GraphicModalCloseResultEnum | undefined> {
    const dialogConfig: MatDialogConfig = {
      width: PencilModalSizeMapping[config.size ?? PencilModalSizeEnum.MEDIUM],
      data: config,
      disableClose: config.dismissable === false,
    };

    return this.matDialog.open(GraphicModalComponent, dialogConfig).afterClosed();
  }

  /**
   * Shows a confirmation dialog with predefined preset configurations
   * @param title The title of the confirmation dialog
   * @param description The description/message of the confirmation dialog
   * @param presetConfirmType The preset configuration type to use
   * @returns Observable<boolean> - true if confirmed, false if cancelled
   */
  showConfirmationModalWithPreset(
    title: string,
    description: string,
    presetConfirmType: PresetConfirmActionEnum,
  ): Observable<boolean> {
    const presetConfirmButtonConfig = this.presetConfirmButtonConfigsMap[presetConfirmType];

    const fullConfig: ConfirmationModalConfig = {
      title,
      description,
      confirmButtonConfig: presetConfirmButtonConfig,
    };

    return this.showConfirmationModal(fullConfig);
  }
}
